import { useEffect, useState } from 'react';
import request from 'lib/request';
import { nameToOption } from 'config/questionOptions';

const ATTRIBUTE_PARAMS = { select: ['id', 'name'], page: 1, pageSize: 0 };

const EMPTY_OPTIONS = { wo: [], company: [] };

// Module-level cache: the attribute name lists rarely change, and the widget
// mounts once per question in edit mode - N mounts collapse to one request.
let cachedOptions = null;
let pendingRequest = null;

const toNameOptions = (response) =>
  (response?.data ?? [])
    .map((attribute) => attribute?.name)
    .filter(Boolean)
    .map(nameToOption);

const loadOptions = () => {
  if (cachedOptions) return Promise.resolve(cachedOptions);

  pendingRequest =
    pendingRequest ??
    Promise.all([
      request.get('/settings/wo-custom-attributes', {
        params: ATTRIBUTE_PARAMS,
      }),
      request.get('/settings/company-attributes', {
        params: ATTRIBUTE_PARAMS,
      }),
    ])
      .then(([woResponse, companyResponse]) => {
        cachedOptions = {
          wo: toNameOptions(woResponse),
          company: toNameOptions(companyResponse),
        };
        return cachedOptions;
      })
      .catch(() => {
        pendingRequest = null;
        return EMPTY_OPTIONS;
      });

  return pendingRequest;
};

/**
 * Loads the attribute names available as a reference-date source for a
 * "Date" quantitative question: work object custom fields and company
 * attributes. Returns options keyed by source type (`wo` / `company`).
 * Pass `enabled: false` to skip fetching (e.g. for non-Date questions).
 */
export default function useMeasureSourceAttributes(enabled = true) {
  const [options, setOptions] = useState(cachedOptions ?? EMPTY_OPTIONS);

  useEffect(() => {
    if (!enabled) return undefined;

    let active = true;
    loadOptions().then((result) => {
      if (active) setOptions(result);
    });

    return () => {
      active = false;
    };
  }, [enabled]);

  return options;
}
